import React, { useState, useEffect } from 'react';
import {
  consentManager,
  getPreferences,
  updatePreferences,
  acceptAll,
  shouldShowBanner
} from '../utils/cookieConsentUtil';
import { updateConsentState } from '../utils/gtmConsent';

/**
 * Cookie consent banner component
 * Shows a banner to new visitors and allows them to manage cookie preferences
 */
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always enabled
    analytics: false, // Default is no consent
    advertising: false // Default is no consent
  });
  
  // Separate state for the settings view checkboxes (checkboxes are inactive by default)
  const [settingsViewPreferences, setSettingsViewPreferences] = useState({
    essential: true, // Always enabled
    analytics: false, // Inactive by default
    advertising: false // Inactive by default
  });
  
  const [expandedSections, setExpandedSections] = useState({
    essential: false,
    analytics: false,
    advertising: false
  });

  // Initialize cookie preferences on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Initialize the consent manager
      consentManager.initialize();
      
      // Get current preferences 
      const savedPrefs = consentManager.getPreferences();
      setCookiePreferences(savedPrefs);
      
      // Check if banner should be shown (for new visitors)
      setShowBanner(consentManager.shouldShowBanner());
      
      // Subscribe to preference changes
      const unsubscribe = consentManager.subscribe((newPreferences) => {
        setCookiePreferences(newPreferences);
      });
      
      return () => {
        unsubscribe();
      };
    }
  }, []);

  // Styling constants
  const CONTAINER_CLASSES = "fixed bottom-4 right-4 left-4 md:left-auto z-50";
  const BANNER_CLASSES = "bg-white w-full md:max-w-md rounded-xl shadow-2xl ml-auto";
  const BUTTON_PRIMARY_CLASSES = "bg-black text-white px-6 py-3 rounded text-sm";
  const BUTTON_OUTLINE_CLASSES = "bg-white border border-black text-black px-6 py-3 rounded text-sm";
  const BUTTON_SECONDARY_CLASSES = "text-black font-medium text-sm hover:underline";
  const TOGGLE_BUTTON_CLASSES = "text-xs mr-2 text-gray-400 font-bold cursor-pointer w-4";
  
  // Toggle switch styling
  const getToggleSwitchClasses = (enabled) => ({
    container: `w-10 h-5 rounded-full relative ${enabled ? 'bg-black' : 'bg-gray-300'} cursor-pointer`,
    slider: `absolute w-4 h-4 rounded-full bg-white top-0.5 transition-all ${enabled ? 'right-0.5' : 'left-0.5'}`
  });

  // Безопасное обновление согласия с проверкой готовности GTM
  const safeUpdateConsent = (analytics, advertising) => {
    // Сначала обновляем локальные настройки
    if (analytics !== undefined && advertising !== undefined) {
      // Затем обновляем GTM
      const gtmResult = updateConsentState(analytics, advertising);
      
      // Закрываем баннер с небольшой задержкой для применения настроек
      setTimeout(() => {
        setShowBanner(false);
      }, 300);
      
      return gtmResult;
    }
    return false;
  };

  // Event handlers
  const handleAcceptAll = () => {
    acceptAll();
    // Обновляем согласие в GTM
    safeUpdateConsent(true, true);
  };
  
  const handleRejectAll = () => {
    // Reject all non-essential cookies
    const rejectedPreferences = {
      essential: true,
      analytics: false,
      advertising: false
    };
    updatePreferences(rejectedPreferences);
    // Обновляем согласие в GTM
    safeUpdateConsent(false, false);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleToggleCookie = (type) => {
    if (type === 'essential') return; // Essential cookies can't be disabled
    
    // Only update settings view state, don't save yet
    setSettingsViewPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleOpenSettings = () => {
    // When opening settings view, initialize checkboxes to current preferences
    // This ensures they match the actual state and are inactive by default
    setSettingsViewPreferences({
      ...cookiePreferences  // Use current saved preferences instead of defaults
    });
    setShowSettings(true);
  };

  const handleConfirm = () => {
    // Update the consent manager with new preferences from settings view
    updatePreferences(settingsViewPreferences);
    // Обновляем согласие в GTM
    safeUpdateConsent(
      settingsViewPreferences.analytics,
      settingsViewPreferences.advertising
    );
  };

  // Cookie section component
  const renderCookieSection = (type, label, description) => {
    const isEssential = type === 'essential';
    const isEnabled = settingsViewPreferences[type]; // Use settings view state
    const isExpanded = expandedSections[type];
    const toggleClasses = getToggleSwitchClasses(isEnabled);
    
    return (
      <div className="py-4">
        <div className="flex justify-between items-center">
          <div 
            className="flex items-center cursor-pointer" 
            onClick={() => toggleSection(type)}
          >
            <button className={TOGGLE_BUTTON_CLASSES}>
              {isExpanded ? '−' : '+'}
            </button>
            <span className="pl-2 text-sm">{label}</span>
          </div>
          
          {isEssential ? (
            <span className="text-blue-500 font-bold text-sm">Always Enabled</span>
          ) : (
            <div className="flex items-center">
              <span className={`mr-2 text-sm ${isEnabled ? 'text-black' : 'text-gray-400'}`}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <div 
                onClick={() => handleToggleCookie(type)}
                className={toggleClasses.container}
              >
                <div 
                  className={toggleClasses.slider}
                  style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                ></div>
              </div>
            </div>
          )}
        </div>
        
        {isExpanded && (
          <div className="mt-2 text-gray-600 pl-8 text-sm">
            {description}
          </div>
        )}
      </div>
    );
  };

  // If banner is closed, don't render anything
  if (!showBanner) {
    return null;
  }

  // Render settings view
  if (showSettings) {
    return (
      <div className={CONTAINER_CLASSES}>
        <div className={BANNER_CLASSES}>
          {/* Header with back arrow */}
          <div className="flex items-center p-4 pl-6 mb-0">
            <button 
              onClick={() => setShowSettings(false)}
              className="text-gray-500 mr-2"
            >
              ←
            </button>
            <span className="text-xs text-gray-500">Cookies managing</span>
          </div>
          
          {/* Settings content */}
          <div className="px-6 pt-1 pb-6">
            <h2 className="text-lg font-bold mb-4">Cookie Settings</h2>
            <p className="mb-2 text-sm">
              Cookies necessary for the correct operation of the site are always enabled.
              Other cookies are configurable.
            </p>
            
            <div className="space-y-4">
              {renderCookieSection(
                'essential', 
                'Essential cookies', 
                'These cookies are necessary for the website to function properly. They enable basic functions like page navigation, access to secure areas of the website, and remembering your preferences. The website cannot function properly without these cookies.'
              )}
              
              {renderCookieSection(
                'analytics', 
                'Analytics cookies', 
                'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They allow us to measure and improve the performance of our site.'
              )}
              
              {renderCookieSection(
                'advertising', 
                'Advertising cookies', 
                'These cookies are used to track visitors across websites. They are set to display targeted advertisements based on your interests and online behavior. They also help measure the effectiveness of advertising campaigns.'
              )}
            </div>
            
            {/* Confirm button */}
            <div className="mt-8">
              <button 
                onClick={handleConfirm}
                className={BUTTON_PRIMARY_CLASSES}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render main banner
  return (
    <div className={CONTAINER_CLASSES}>
      <div className={BANNER_CLASSES}>
        <div className="p-6">
          <h2 className="text-lg font-bold mb-1">Cookies managing</h2>
          <p className="mb-6 text-sm">
            We use cookies to provide the best site experience. By continuing to use the site, you consent to our cookie policy.
          </p>
          <div className="flex gap-4">
            <button 
              onClick={handleAcceptAll}
              className={BUTTON_PRIMARY_CLASSES}
            >
              Accept All
            </button>
            <button 
              onClick={handleRejectAll}
              className={BUTTON_OUTLINE_CLASSES}
            >
              Reject All
            </button>
            <button 
              onClick={handleOpenSettings}
              className={BUTTON_SECONDARY_CLASSES}
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;